import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Coins, Download, EyeOff, FileSpreadsheet } from 'lucide-react';
import { adminReports } from '@/api/admin';
import type { Money } from '@/api/types';
import type {
  PayoutStatus,
  ProfitShareChannel,
  ProfitShareMonthly,
  ProfitSharePayoutItem,
  ProfitShareUser,
} from '@/api/extras';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Pill } from '@/components/widgets/Pill';
import { toast } from '@/components/ui/sonner';
import { ApiError } from '@/lib/http';
import { fmtMoney, moneyToNumber } from '@/lib/format';
import { downloadCsv } from '@/lib/csv';
import { UserSettlementDialog } from './profit-share/UserSettlementDialog';

/**
 * 月度分润(v3 §2.1/§11.1)。合作伙伴按其虚拟账户**已实现盈利**的 10% 分润。
 * 口径:仅已实现 · 扣交易服务费后 · 无高水位线(亏损月不分润不累计);月界 = 自然月北京 03:00 交易日界。
 *
 * 结算核对视角(2026-07):
 * - Tab = 渠道(首个「全部渠道」为总览),切到某渠道即看该渠道每个人的分润明细;
 * - 考核完成度只作**人工判断依据**,不影响金额 —— 系统停机 / App 更新导致的未达标通常仍应照发;
 * - 发放决定(待定/已发/不发 + 实发额 + 备注)就地保存,落库可追溯;
 * - Excel 导出由后端生成多 sheet(汇总 / 人员汇总 / 每人一页)。
 */

/** 当前(offset=0)/相邻月份的 YYYY-MM(本机时区近似)。 */
function ymLabel(offset = 0): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

const cents = (value: number): Money => ({ value, digits: 2 });

const ALL_TAB = '__all__';
const NO_CHANNEL = '__none__';

const PAYOUT_LABEL: Record<PayoutStatus, string> = {
  PENDING: '待定',
  PAID: '已发放',
  WITHHELD: '不发放',
};

function channelKey(c: Pick<ProfitShareChannel, 'inviteCodeId'>): string {
  return c.inviteCodeId ?? NO_CHANNEL;
}

function channelLabel(c: Pick<ProfitShareChannel, 'channelAlias' | 'inviteCode'>): string {
  return c.channelAlias || c.inviteCode || '无渠道';
}

export function ProfitShare() {
  const [ym, setYm] = useState(ymLabel());
  const [tab, setTab] = useState<string>(ALL_TAB);
  const [detailUserId, setDetailUserId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  // 默认剔除「已归档人员 + 已停用渠道的全部人员」,且**连金额一起剔除**(不计入渠道小计与总合计)。
  // 打开开关把这两类人连同其金额一起拉回来显示。两个后端参数由这一个开关同步控制。
  const [showExcluded, setShowExcluded] = useState(false);

  const [yStr, mStr] = ym.split('-');
  const year = Number(yStr);
  const month = Number(mStr);
  const validYm = Number.isFinite(year) && Number.isFinite(month) && month >= 1 && month <= 12;

  // include=all:把「当月无结算但有考核记录」的在册用户也列出来(分润 0),按花名册核对不漏人。
  // showExcluded 必须进 queryKey,否则切开关不会重新拉数。
  const q = useQuery({
    queryKey: ['admin', 'profit-share', year, month, showExcluded],
    queryFn: () =>
      adminReports.profitShareMonthly(year, month, undefined, {
        include: 'all',
        includeArchived: showExcluded,
        includeDisabledChannels: showExcluded,
      }),
    enabled: validYm,
  });
  const data = q.data;

  // 渠道 Tab:后端已按应分润降序给出 channels[];老后端(无该字段)回退按 users 前端归并。
  const channels: ProfitShareChannel[] = useMemo(() => {
    if (data?.channels?.length) return data.channels;
    if (!data) return [];
    const map = new Map<string, ProfitShareChannel>();
    for (const u of data.users) {
      const key = u.inviteCodeId ?? NO_CHANNEL;
      const g = map.get(key) ?? {
        inviteCodeId: u.inviteCodeId,
        inviteCode: u.inviteCode,
        channelAlias: u.channelAlias,
        channelDisabled: u.channelDisabled ?? false,
        userCount: 0,
        realizedNet: cents(0),
        serviceFee: cents(0),
        share: cents(0),
        paidTotal: cents(0),
        assessment: { fullPassUsers: 0, partialUsers: 0, noRecordUsers: 0 },
        payout: { pending: 0, paid: 0, withheld: 0 },
      };
      g.userCount += 1;
      g.realizedNet = cents(Number(g.realizedNet.value) + Number(u.realizedNet.value));
      g.share = cents(Number(g.share.value) + Number(u.share.value));
      map.set(key, g);
    }
    return [...map.values()].sort((a, b) => Number(b.share.value) - Number(a.share.value));
  }, [data]);

  // 切月后若原渠道 Tab 不存在了,回落到「全部渠道」,避免空白页。
  useEffect(() => {
    if (tab !== ALL_TAB && !channels.some((c) => channelKey(c) === tab)) setTab(ALL_TAB);
  }, [channels, tab]);

  const users = useMemo(() => {
    const list = data?.users ?? [];
    const scoped = tab === ALL_TAB ? list : list.filter((u) => (u.inviteCodeId ?? NO_CHANNEL) === tab);
    return [...scoped].sort((a, b) => Number(b.share.value) - Number(a.share.value));
  }, [data, tab]);

  // 防漏发的关键信号:开关关着时被剔除的人**连金额都不在上面的合计里**,必须直接可见(不做 tooltip)。
  // 老后端没有 hiddenByFilter → undefined 就不显示 —— 读不到不等于 0 人,绝不回退成 0 装作没隐藏。
  const hiddenByFilter = data?.hiddenByFilter;
  const hidden = !showExcluded && hiddenByFilter && hiddenByFilter.userCount > 0 ? hiddenByFilter : undefined;
  // 已发放却被隐藏的钱:老后端没有 paid 字段 → undefined,那就不提这句(读不到 ≠ 没发过)。
  const hiddenPaid = hidden?.paid && Number(hidden.paid.value) > 0 ? hidden.paid : undefined;

  // 当前 Tab = 导出范围。CSV 与 Excel 都只导出当前可见的这批人,故文件里必须写清楚"哪个渠道 + 哪个口径"。
  const activeChannel = tab === ALL_TAB ? undefined : channels.find((c) => channelKey(c) === tab);
  const scopeLabel = tab === ALL_TAB ? '全部渠道' : activeChannel ? channelLabel(activeChannel) : '当前渠道';

  // 文件名的渠道段,与后端 Excel 逐字对齐(profit_share.rs::export_name_suffix 的中文名后缀):
  // 口径段 `-全量` / `-默认口径`,其后接渠道段 `-渠道{inviteCodeId}` / `-无渠道`,全部渠道时为空。
  // 跟后端一样按 id 拼而不是别名 —— 别名可空、可重复、可含非法文件名字符,不能作唯一标识。
  // 没这一段的话,同一天从两个渠道 Tab 各导一次会重名,浏览器落成 xxx(1).csv,事后分不出哪份是哪个渠道。
  const fileChannelSuffix = tab === ALL_TAB ? '' : tab === NO_CHANNEL ? '-无渠道' : `-渠道${tab}`;

  const exportCsv = () =>
    downloadCsv(
      // 两种口径导出的列完全一样、合计不同,文件名不区分的话事后分不出手里这份是哪一份。
      `profit-share-${ym}${showExcluded ? '-全量' : '-默认口径'}${fileChannelSuffix}`,
      users,
      [
        { key: 'userId', header: '用户ID' },
        { key: 'nickname', header: '昵称', format: (_, r) => r.nickname ?? '' },
        { key: 'phone', header: '手机号', format: (_, r) => r.phone ?? '' },
        { key: 'channelAlias', header: '渠道', format: (_, r) => r.channelAlias ?? r.inviteCode ?? '' },
        // 与「状态」并列的离线核对列:该行是不是"停用渠道下的人"(默认口径里根本不会出现,只有开关打开才有值)。
        { key: 'channelDisabled', header: '渠道已停用', format: (_, r) => (r.channelDisabled ? '是' : '') },
        { key: 'userStatus', header: '状态', format: (_, r) => (r.archived ? '已归档' : (r.userStatus ?? '')) },
        { key: 'virtualBalance', header: '虚拟余额', format: (_, r) => moneyToNumber(r.virtualBalance).toFixed(2) },
        { key: 'serviceFee', header: '交易服务费', format: (_, r) => (r.serviceFee ? moneyToNumber(r.serviceFee).toFixed(2) : '') },
        { key: 'realizedNet', header: '已实现净(扣费后)', format: (_, r) => moneyToNumber(r.realizedNet).toFixed(2) },
        { key: 'settlements', header: '结算笔数' },
        { key: 'share', header: '应分润(10%)', format: (_, r) => moneyToNumber(r.share).toFixed(2) },
        { key: 'assessed', header: '考核天数', format: (_, r) => String(r.assessment?.assessedDays ?? '') },
        { key: 'passed', header: '达标天数', format: (_, r) => String(r.assessment?.passedDays ?? '') },
        { key: 'passRate', header: '达标率', format: (_, r) => (r.assessment?.passRate != null ? `${(r.assessment.passRate * 100).toFixed(1)}%` : '') },
        { key: 'payoutStatus', header: '发放状态', format: (_, r) => PAYOUT_LABEL[r.payout?.status ?? 'PENDING'] },
        { key: 'paidAmount', header: '实发额', format: (_, r) => (r.payout?.paidAmount ? moneyToNumber(r.payout.paidAmount).toFixed(2) : '') },
        { key: 'payoutNote', header: '备注', format: (_, r) => r.payout?.note ?? '' },
      ],
      {
        // 首行口径说明:CSV 会脱离页面单独流转(邮件 / 群里转发),光看列名分不出这是默认口径还是全量,
        // 更看不出"有人被隐藏了"。文件名只能承载口径二选一,隐藏了几个人 / 多少钱只能写在这里。
        // 代价是表头落到第 2 行,见 lib/csv.ts 的 CsvOptions.note 说明。
        note:
          `月度分润 ${ym} · 范围:${scopeLabel} · 口径:` +
          (showExcluded
            ? '全量(含「已停用渠道下全部人员」与「已归档人员」)'
            : '默认(已剔除「已停用渠道下全部人员」与「已归档人员」;其金额不计入本表任何合计)') +
          (hidden
            ? ` · 全平台口径下另有 ${hidden.userCount} 人被隐藏、应分润合计 ${fmtMoney(hidden.share)}` +
              (hiddenPaid ? `(其中已发放 ${fmtMoney(hiddenPaid)})` : '') +
              ' —— 均不在本表内'
            : ''),
      },
    );

  const exportXlsx = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await adminReports.profitShareExportXlsx(year, month, {
        // 当前 Tab 即导出范围。「无渠道」桶传 'none'(后端解析成 invite_code_id IS NULL),
        // 不能传 undefined —— 那会连带导出全部渠道,核对时对不上。
        inviteCodeId: tab === ALL_TAB ? undefined : tab === NO_CHANNEL ? 'none' : tab,
        include: 'all',
        // 必须跟随页面开关,否则导出与页面对不上账。
        includeArchived: showExcluded,
        includeDisabledChannels: showExcluded,
      });
    } catch (e) {
      // 人数超过每人一页上限时后端返 400,错误信息里带处置建议(先按渠道筛选)。
      toast.error('导出失败', { description: e instanceof ApiError ? e.message : '未知错误' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-ink-0 flex items-center gap-2">
            <Coins className="h-5 w-5 text-brand" />
            月度分润
          </h1>
          <p className="text-sm text-ink-2 mt-1">
            合作伙伴按虚拟账户已实现盈利的 10% 分润 · 口径:仅已实现 · 扣交易服务费后 · 无高水位线 · 月界北京 03:00
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={users.length === 0} onClick={exportCsv}>
            <Download className="h-4 w-4" />
            导出 CSV
          </Button>
          <Button size="sm" disabled={!validYm || exporting} onClick={exportXlsx}>
            <FileSpreadsheet className="h-4 w-4" />
            {exporting ? '导出中…' : '导出 Excel'}
          </Button>
        </div>
      </header>

      {/* 分润是真金白银付款,而基数是虚拟账本口径 —— 脱钩期的那部分没有真实对冲支撑,付款前必须知情。 */}
      <div className="rounded-md border border-warn/40 bg-warn/5 px-3 py-2 text-xs leading-relaxed text-warn-dim">
        <span className="font-semibold">口径提示</span>:分润基数是用户虚拟账本的已实现盈利,
        <span className="font-semibold">不区分该笔背后是否有真实对冲支撑</span>。若当月执行过「真实账户平仓」,
        脱钩期内产生的用户盈利由平台全额承担(真实账户没有对应成交),但仍按 10% 计入本表。
        分润为真金白银付款,请结合「平台经营」页的真实净盈亏与未对冲敞口一并判断。
        <div className="mt-1">
          <span className="font-semibold">当前范围</span>:
          {showExcluded
            ? '已包含「已停用渠道下全部人员」与「已归档人员」,其金额一并计入渠道小计与总合计。'
            : '已剔除「已停用渠道下全部人员」与「已归档人员」,其金额不计入渠道小计与总合计(无渠道人员不受渠道停用影响,始终保留)。'}
        </div>
      </div>

      {/* 月界错位告警:分润月界固定 03:00,考核日界跟随 force_close,不等时后端回文案。 */}
      {data?.calendarWarning && (
        <div className="rounded-md border border-down/40 bg-down/5 px-3 py-2 text-xs leading-relaxed text-down">
          <span className="font-semibold">月界口径告警</span>:{data.calendarWarning}
        </div>
      )}

      <section className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-ink-2">
          月份
          <input
            type="month"
            value={ym}
            onChange={(e) => setYm(e.target.value)}
            className="ml-2 w-40 rounded-md border border-line bg-surface-1 px-2 py-1 text-ink-0 num"
          />
        </label>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={() => setYm(ymLabel(-1))}>上月</Button>
          <Button variant="outline" size="sm" onClick={() => setYm(ymLabel(0))}>本月</Button>
        </div>
        <Button variant="outline" onClick={() => q.refetch()} disabled={q.isFetching || !validYm}>
          {q.isFetching ? '加载中…' : '查询'}
        </Button>
        <label className="ml-2 flex items-center gap-2 text-sm text-ink-2">
          <Switch checked={showExcluded} onCheckedChange={setShowExcluded} />
          显示已停用渠道 / 已归档人员
          <span className="text-xs text-ink-3">(关闭时这两类人的金额也不计入本页合计与导出)</span>
        </label>
      </section>

      {/*
        被隐藏 = 合计里少算了这些人的钱。分润是真金白银付款,漏发比多算更难挽回,故这里直接摊开(不做 tooltip)。

        ⚠ 口径:页面查询恒不传 inviteCodeId(渠道切分在前端做),所以 hiddenByFilter 永远是**全平台**汇总,
        拆不到单个渠道。处理办法是"换措辞"而不是"收进全部渠道 Tab 里":后者会让长期泡在某个渠道 Tab 上的人
        彻底看不到这个信号(漏发风险 > 措辞不精确)。因此渠道 Tab 下明说这是全平台数、且本页合计与导出
        只覆盖本渠道可见人员 —— 该 Tab 的导出按 inviteCodeId 走,被隐藏的人可能根本不属于这个渠道,
        再说「未计入本页合计与导出」就是假的。
      */}
      {hidden && (
        <div className="flex items-start gap-2 rounded-md border border-down/60 bg-down/10 px-3 py-2 text-sm leading-relaxed text-down">
          <EyeOff className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <span className="font-semibold">
              当前口径已隐藏 {hidden.userCount} 人(其中已归档 {hidden.archivedUsers} 人、停用渠道{' '}
              {hidden.disabledChannelUsers} 人
              {hidden.archivedUsers + hidden.disabledChannelUsers > hidden.userCount ? ',两类有重叠' : ''}),应分润合计{' '}
              {fmtMoney(hidden.share)}
              {tab === ALL_TAB ? ' 未计入本页合计与导出。' : ' 未计入任何统计(全平台口径)。'}
            </span>
            {/* 已发放却被隐藏最危险:钱已经付出去了,却不在「已发放合计」里,核对时容易被当成没发过而重复发放。 */}
            {hiddenPaid && (
              <span className="ml-1 font-semibold">
                其中已发放 {fmtMoney(hiddenPaid)} 同样不计入
                {tab === ALL_TAB ? '上方「已发放合计」' : '「已发放合计」'}。
              </span>
            )}
            <span className="ml-1">
              {tab === ALL_TAB
                ? '需要核对请打开「显示已停用渠道 / 已归档人员」开关。'
                : '该数字是全平台汇总、拆不到单个渠道;本渠道页的合计与导出只覆盖本渠道可见人员。需要核对请切到「全部渠道」并打开「显示已停用渠道 / 已归档人员」开关。'}
            </span>
          </div>
        </div>
      )}

      {q.isError && <p className="text-sm text-down">加载失败:{(q.error as Error)?.message}</p>}

      {data && (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value={ALL_TAB}>全部渠道</TabsTrigger>
            {channels.map((c) => (
              <TabsTrigger key={channelKey(c)} value={channelKey(c)}>
                {channelLabel(c)}
                {c.channelDisabled && <span className="ml-1 text-xs text-ink-3">(已停用)</span>}
                <span className="ml-1.5 text-xs text-ink-3 num">{c.userCount}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={ALL_TAB} className="space-y-4">
            <OverviewCards data={data} />
            <ChannelTable channels={channels} onPick={setTab} />
            <UserTable
              users={users}
              year={year}
              month={month}
              title={`${data.year} 年 ${data.month} 月 · 全部人员分润明细(按应分润降序)`}
              onOpen={setDetailUserId}
            />
          </TabsContent>

          {channels.map((c) => (
            <TabsContent key={channelKey(c)} value={channelKey(c)} className="space-y-4">
              <ChannelCards channel={c} />
              <UserTable
                users={users}
                year={year}
                month={month}
                title={`${channelLabel(c)}${c.channelDisabled ? '(渠道已停用)' : ''} · ${data.year} 年 ${data.month} 月 分润明细`}
                onOpen={setDetailUserId}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}

      <UserSettlementDialog
        userId={detailUserId}
        year={year}
        month={month}
        onClose={() => setDetailUserId(null)}
      />
    </div>
  );
}

function StatCard({ label, value, sub, tone = 'ink' }: { label: string; value: string; sub?: string; tone?: 'ink' | 'up' | 'brand' }) {
  const cls = tone === 'up' ? 'text-up' : tone === 'brand' ? 'text-brand' : 'text-ink-0';
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-ink-2">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${cls}`}>{value}</div>
        {sub && <p className="text-xs text-ink-3 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function OverviewCards({ data }: { data: ProfitShareMonthly }) {
  const a = data.assessmentSummary;
  const paidUsers = data.users.filter((u) => u.payout?.status === 'PAID').length;
  const pendingUsers = data.users.filter((u) => (u.payout?.status ?? 'PENDING') === 'PENDING').length;
  return (
    <section className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
      <StatCard label="当月合计已实现净(扣费后)" value={fmtMoney(data.totalRealizedNet, { sign: true })} />
      <StatCard label="当月合计交易服务费" value={data.totalServiceFee ? fmtMoney(data.totalServiceFee) : '—'} />
      <StatCard label="当月合计应分润(10%)" value={fmtMoney(data.totalShare)} tone="up" />
      <StatCard
        label="全月考核达标人数"
        value={a ? String(a.fullPassUsers) : '—'}
        sub={a ? `部分达标 ${a.partialUsers} · 无记录 ${a.noRecordUsers}` : undefined}
        tone="brand"
      />
      <StatCard label="已发放合计" value={data.totalPaid ? fmtMoney(data.totalPaid) : '—'} sub={`已发 ${paidUsers} 人`} />
      {/*
        「人在册」是错的:默认口径下已归档 / 停用渠道的人连人带金额都被剔除了,users.length 只是当前可见人数,
        与上方隐藏提示条并读会让人以为"在册就这么多"。这里只陈述可见数,在册口径由隐藏提示条负责补齐。
      */}
      <StatCard label="待定人数" value={String(pendingUsers)} sub={`当前可见 ${data.users.length} 人`} />
    </section>
  );
}

function ChannelCards({ channel }: { channel: ProfitShareChannel }) {
  return (
    <section className="grid grid-cols-1 md:grid-cols-4 gap-3">
      <StatCard label="本渠道已实现净(扣费后)" value={fmtMoney(channel.realizedNet, { sign: true })} sub={`${channel.userCount} 人`} />
      <StatCard label="本渠道应分润(10%)" value={fmtMoney(channel.share)} tone="up" />
      <StatCard
        label="考核全月达标"
        value={String(channel.assessment.fullPassUsers)}
        sub={`部分达标 ${channel.assessment.partialUsers} · 无记录 ${channel.assessment.noRecordUsers}`}
        tone="brand"
      />
      <StatCard
        label="发放进度"
        value={`${channel.payout.paid}/${channel.userCount}`}
        sub={`待定 ${channel.payout.pending} · 不发 ${channel.payout.withheld} · 已发 ${fmtMoney(channel.paidTotal)}`}
      />
    </section>
  );
}

function ChannelTable({ channels, onPick }: { channels: ProfitShareChannel[]; onPick: (key: string) => void }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">按渠道小计(点击行查看该渠道明细)</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-ink-3 border-b border-line">
              <th className="py-2 pr-4">渠道</th>
              <th className="py-2 pr-4 text-right">人数</th>
              <th className="py-2 pr-4 text-right">已实现净(扣费后)</th>
              <th className="py-2 pr-4 text-right">应分润合计(10%)</th>
              <th className="py-2 pr-4 text-right">全月达标</th>
              <th className="py-2 pr-4 text-right">发放进度</th>
              <th className="py-2 pr-4 text-right">已发放合计</th>
            </tr>
          </thead>
          <tbody>
            {channels.length === 0 && (
              <tr>
                <td colSpan={7} className="py-6 text-center text-ink-3">当月无结算记录</td>
              </tr>
            )}
            {channels.map((c) => {
              const net = Number(c.realizedNet.value);
              return (
                <tr
                  key={channelKey(c)}
                  className="border-b border-line/50 row-hover cursor-pointer"
                  onClick={() => onPick(channelKey(c))}
                >
                  <td className="py-2 pr-4 text-ink-1">
                    {channelLabel(c)}
                    {c.channelDisabled && (
                      <Pill tone="warn" className="ml-2" title="该邀请码已停用;默认口径下整条渠道连人带金额被剔除">
                        已停用
                      </Pill>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-right num text-ink-2">{c.userCount}</td>
                  <td className={`py-2 pr-4 text-right num ${net >= 0 ? 'text-up' : 'text-down'}`}>
                    {fmtMoney(c.realizedNet, { sign: true })}
                  </td>
                  <td className="py-2 pr-4 text-right num font-medium text-ink-0">{fmtMoney(c.share)}</td>
                  <td className="py-2 pr-4 text-right num text-ink-2">
                    {c.assessment.fullPassUsers}/{c.userCount}
                  </td>
                  <td className="py-2 pr-4 text-right num text-ink-2">
                    {c.payout.paid}/{c.userCount}
                  </td>
                  <td className="py-2 pr-4 text-right num text-ink-1">{fmtMoney(c.paidTotal)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

/** 考核完成度小标签:全月达标绿 / 有未达标红 / 无记录灰。tooltip 列出未达标日期。 */
function AssessmentCell({ u }: { u: ProfitShareUser }) {
  const a = u.assessment;
  if (!a || a.assessedDays === 0) {
    return <span className="text-ink-3 text-xs" title="当月无考核记录(可能整月处于停用/归档)">—</span>;
  }
  const rate = a.passRate != null ? `${(a.passRate * 100).toFixed(1)}%` : '—';
  const title =
    a.failedDays > 0
      ? `未达标 ${a.failedDays} 天:${a.failedDates.join('、')}${a.overriddenDays > 0 ? ` · 已改判 ${a.overriddenDays} 天` : ''}`
      : `全月达标${a.overriddenDays > 0 ? ` · 已改判 ${a.overriddenDays} 天` : ''}`;
  return (
    <span title={title} className="inline-flex items-center gap-1.5">
      <Pill tone={a.failedDays === 0 ? 'up' : 'down'}>
        {a.passedDays}/{a.assessedDays}
      </Pill>
      <span className="text-xs text-ink-3 num">{rate}</span>
    </span>
  );
}

function UserTable({
  users,
  year,
  month,
  title,
  onOpen,
}: {
  users: ProfitShareUser[];
  year: number;
  month: number;
  title: string;
  onOpen: (userId: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-ink-3 border-b border-line">
              <th className="py-2 pr-4">用户</th>
              <th className="py-2 pr-4">渠道</th>
              <th className="py-2 pr-4 text-right">结算笔数</th>
              <th className="py-2 pr-4 text-right">交易服务费</th>
              <th className="py-2 pr-4 text-right">已实现净(扣费后)</th>
              <th className="py-2 pr-4 text-right">应分润(10%)</th>
              <th className="py-2 pr-4">考核达标</th>
              <th className="py-2 pr-4">发放</th>
              <th className="py-2 pr-4 text-right">实发额</th>
              <th className="py-2 pr-4"></th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr>
                <td colSpan={10} className="py-6 text-center text-ink-3">当月无记录</td>
              </tr>
            )}
            {users.map((u) => (
              <UserRow key={u.userId} u={u} year={year} month={month} onOpen={onOpen} />
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function UserRow({
  u,
  year,
  month,
  onOpen,
}: {
  u: ProfitShareUser;
  year: number;
  month: number;
  onOpen: (userId: string) => void;
}) {
  const qc = useQueryClient();
  const net = Number(u.realizedNet.value);
  const status: PayoutStatus = u.payout?.status ?? 'PENDING';

  // 发放状态就地保存。写操作在 query-client 已配置不重试,天然防重复提交。
  const save = useMutation({
    mutationFn: (item: ProfitSharePayoutItem) => adminReports.profitSharePayouts(year, month, [item]),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'profit-share', year, month] });
      qc.invalidateQueries({ queryKey: ['admin', 'profit-share-user'] });
    },
    onError: (e) => toast.error('保存失败', { description: e instanceof ApiError ? e.message : '' }),
  });

  const setStatus = (next: PayoutStatus) => {
    if (next === 'WITHHELD' && !(u.payout?.note ?? '').trim()) {
      toast.error('不发放需要填写备注', { description: '请先在备注里写明原因(结算争议时要能回答为什么没发)' });
      return;
    }
    save.mutate({
      userId: u.userId,
      status: next,
      // 已发放:沿用已填实发额,未填则由后端取系统计算的应分润。
      paidAmount: next === 'PAID' ? (u.payout?.paidAmount ?? null) : null,
      note: u.payout?.note ?? null,
    });
  };

  return (
    <tr className="border-b border-line/50 row-hover">
      <td className="py-2 pr-4">
        <button className="text-left hover:text-brand" onClick={() => onOpen(u.userId)} title="查看该用户当月逐笔交易与考核明细">
          <div className="text-ink-1">{u.nickname || u.phone || `用户${u.userId}`}</div>
          <div className="text-xs text-ink-3 num">
            {u.phone || `ID ${u.userId}`}
            {u.archived && <span className="ml-1 text-ink-3">· 已归档</span>}
            {/* 与「已归档」同一行、同样低调:这两类人默认根本不出现,出现即说明开关是打开的。 */}
            {u.channelDisabled && <span className="ml-1 text-ink-3">· 渠道已停用</span>}
          </div>
        </button>
      </td>
      <td className="py-2 pr-4 text-ink-2">{u.channelAlias ?? u.inviteCode ?? '—'}</td>
      <td className="py-2 pr-4 text-right num text-ink-2">{u.settlements}</td>
      <td className="py-2 pr-4 text-right num text-ink-2">
        {u.serviceFee && Number(u.serviceFee.value) > 0 ? fmtMoney(u.serviceFee) : '—'}
      </td>
      <td className={`py-2 pr-4 text-right num ${net >= 0 ? 'text-up' : 'text-down'}`}>
        {fmtMoney(u.realizedNet, { sign: true })}
      </td>
      <td className="py-2 pr-4 text-right num font-medium text-ink-0">
        {Number(u.share.value) > 0 ? fmtMoney(u.share) : '—'}
      </td>
      <td className="py-2 pr-4">
        <AssessmentCell u={u} />
      </td>
      <td className="py-2 pr-4">
        <select
          value={status}
          disabled={save.isPending}
          onChange={(e) => setStatus(e.target.value as PayoutStatus)}
          className="h-8 rounded-md border border-line bg-bg-3 px-2 text-sm text-ink-1"
          title="发放与否由人工判定;未达标不影响金额计算"
        >
          <option value="PENDING">待定</option>
          <option value="PAID">已发放</option>
          <option value="WITHHELD">不发放</option>
        </select>
      </td>
      <td className="py-2 pr-4 text-right num text-ink-1">
        {u.payout?.paidAmount ? fmtMoney(u.payout.paidAmount) : '—'}
      </td>
      <td className="py-2 pr-4 text-right">
        <Button variant="ghost" size="sm" onClick={() => onOpen(u.userId)}>
          明细
        </Button>
      </td>
    </tr>
  );
}
